import { useQuery } from "@tanstack/react-query";
import { format } from "date-fns";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/shadcn/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/shadcn/table";
import { leadsApi } from "@/api/LeadsApi";

export function LeadsPage() {
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["leads"],
    queryFn: () => leadsApi.getLeads(),
  });

  const formatDate = (dateString: string | null) => {
    if (!dateString) return "—";
    try {
      return format(new Date(dateString), "MMM dd, yyyy HH:mm");
    } catch {
      return dateString;
    }
  };

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-full w-full p-6">
        <Card className="w-full max-w-7xl">
          <CardHeader>
            <CardTitle>Leads</CardTitle>
            <CardDescription>Loading leads...</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-center py-8">
              <div className="text-muted-foreground">Loading...</div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex flex-col items-center justify-center min-h-full w-full p-6">
        <Card className="w-full max-w-7xl">
          <CardHeader>
            <CardTitle>Leads</CardTitle>
            <CardDescription>Error loading leads</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-center py-8">
              <div className="text-destructive">
                {error instanceof Error
                  ? error.message
                  : "Failed to load leads"}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const leads = data?.leads ?? [];

  return (
    <div className="flex flex-col min-h-full w-full p-6">
      <Card className="w-full max-w-7xl mx-auto shadow-[var(--shadow-depth-md)]">
        <CardHeader>
          <CardTitle className="text-3xl font-bold text-black-1 mb-2">
            Leads
          </CardTitle>
          <CardDescription className="text-lg text-gray-1">
            View and manage all leads from the CRM system
          </CardDescription>
        </CardHeader>
        <CardContent>
          {leads.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12">
              <p className="text-muted-foreground">No leads found</p>
            </div>
          ) : (
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="px-6 py-4">Name</TableHead>
                    <TableHead className="px-6 py-4">Email</TableHead>
                    <TableHead className="px-6 py-4">Phone</TableHead>
                    <TableHead className="px-6 py-4">Lead Status</TableHead>
                    <TableHead className="px-6 py-4">User Type</TableHead>
                    <TableHead className="px-6 py-4">Lead Source</TableHead>
                    <TableHead className="px-6 py-4">Follow Up Date</TableHead>
                    <TableHead className="px-6 py-4">Created At</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {leads.map((lead) => (
                    <TableRow key={lead.id}>
                      <TableCell className="px-6 py-4 font-medium">
                        {lead.name}
                      </TableCell>
                      <TableCell className="px-6 py-4">
                        {lead.email || "—"}
                      </TableCell>
                      <TableCell className="px-6 py-4">
                        {lead.phone || "—"}
                      </TableCell>
                      <TableCell className="px-6 py-4">
                        {lead.leadStatus}
                      </TableCell>
                      <TableCell className="px-6 py-4">
                        {lead.userType}
                      </TableCell>
                      <TableCell className="px-6 py-4">
                        {lead.leadSource}
                      </TableCell>
                      <TableCell className="px-6 py-4">
                        {formatDate(lead.followUpDate)}
                      </TableCell>
                      <TableCell className="px-6 py-4">
                        {formatDate(lead.createdAt)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
